import { useRemoveClientLogo, useUploadClientLogo } from '@/api/hooks';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api';
import type { Client } from '@/types/api';
import { Building2, ImageIcon, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';

type ClientLogoEditorProps = {
  client: Client;
};

function isImageFile(file: File): boolean {
  if (file.type.startsWith('image/')) return true;
  return /\.(jpe?g|png|gif|webp)$/i.test(file.name);
}

export function ClientLogoEditor({ client }: ClientLogoEditorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadLogo = useUploadClientLogo();
  const removeLogo = useRemoveClientLogo();
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loading = uploadLogo.isPending || removeLogo.isPending;
  const brandLabel = client.trade_name || client.legal_name;

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setError('');
    setMessage('');

    if (!isImageFile(file)) {
      setError('Selecione uma imagem (JPG, PNG ou WebP).');
      return;
    }

    try {
      await uploadLogo.mutateAsync(file);
      setMessage('Logo da organização atualizada.');
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao enviar a logo.'));
    }
  }

  async function handleRemove() {
    setError('');
    setMessage('');
    try {
      await removeLogo.mutateAsync();
      setMessage('Logo removida. O menu voltará a exibir a marca padrão.');
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao remover a logo.'));
    }
  }

  return (
    <div className="space-y-4">
      {message && <Alert variant="success">{message}</Alert>}
      {error && <Alert variant="error">{error}</Alert>}

      <div>
        <p className="text-sm font-medium text-manejai-bark mb-1">Logo da organização</p>
        <p className="text-xs text-manejai-bark/60 mb-3">
          Aparece no topo do menu lateral, em fundo branco, para todos os usuários da conta.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex h-16 w-44 items-center justify-center rounded-xl border border-manejai-sand bg-white px-3">
          {client.logo_url ? (
            <img
              src={client.logo_url}
              alt={brandLabel}
              className="max-h-12 max-w-full object-contain"
            />
          ) : (
            <div className="flex flex-col items-center gap-1 text-manejai-bark/40">
              <Building2 className="h-6 w-6" />
              <span className="text-[10px]">Sem logo</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={loading}
          >
            <ImageIcon className="h-4 w-4" />
            Enviar logo
          </Button>
          {client.logo_url && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              loading={loading}
            >
              <Trash2 className="h-4 w-4" />
              Remover
            </Button>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
